import { Navigate, type Params, type RouteObject } from 'react-router-dom'
import ErrorBoundary from 'shared/components/ErrorBoundary'
import client, { preloadQuery } from 'shared/graphql/client'
import { UpdateProfileViewHistory } from './graphql/candidateProfile.gql'
import { GetCompany } from './graphql/companies.gql'
import { TrackRecruiterRosterView } from './graphql/trackRecruiterRosterView.gql'
import CandidateProfile from 'recruiter/pre-ralph/people'
import AdminDashboard from './pages/Admin'
import Companies from './pages/Companies'
import ClientPermissions from './pages/Companies/ClientPermissions'
import CompanyOverview from './pages/Companies/CompanyOverview'
import { SearchView } from './pages/Research'
import MyRosters from './pages/Rosters'
import RosterProfile from './pages/Rosters/RosterProfile'
import SlackLink from './pages/SlackLink'

export const recruiterRouteConfig = {
    candidates: {
        path: '/people',
    },
    rosters: {
        path: '/searches',
    },
    companies: {
        path: '/companies',
    },
    research: {
        path: '/research',
    },
    admin: {
        path: '/admin',
    },
    slackLink: {
        path: '/slack/link',
    },
}

export interface CandidateRouteParams extends Params {
    candidateId?: string
}

export interface RosterRouteParams extends Params {
    rosterId?: string
}

const handleCandidateProfileRouteLoad = async ({ params }) => {
    const { candidateId } = params
    await client.mutate({
        mutation: UpdateProfileViewHistory,
        variables: { candidateId: candidateId ?? '' },
    })

    return null
}

const handleRosterProfileRouteLoad = async ({ params }) => {
    const { rosterId }: RosterRouteParams = params

    await client.mutate({
        mutation: TrackRecruiterRosterView,
        variables: { rosterId: rosterId ?? '' },
    })

    return null
}

const RecruiterRoutes: RouteObject[] = [
    // Search & Filter routes
    {
        path: recruiterRouteConfig.research.path,
        element: <Navigate to="candidates" replace />,
    },
    {
        path: `${recruiterRouteConfig.research.path}/candidates`,
        element: <SearchView initialTab="candidates" />,
    },
    {
        path: `${recruiterRouteConfig.research.path}/searches`,
        element: <SearchView initialTab="rosters" />,
    },

    // Candidate Profile routes
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId`,
        element: <Navigate to="overview" replace />,
        errorElement: <ErrorBoundary />,
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/overview`,
        element: <Navigate to="on" replace />,
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/overview/on`,
        element: (
            <CandidateProfile
                initialTab="overview"
                initialRosterTableView="on"
            />
        ),
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/overview/all`,
        element: (
            <CandidateProfile
                initialTab="overview"
                initialRosterTableView="all"
            />
        ),
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/overview-old`,
        element: <Navigate to="on" replace />,
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/overview-old/on`,
        element: (
            <CandidateProfile
                initialTab="overview"
                initialRosterTableView="on"
            />
        ),
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/overview-old/all`,
        element: (
            <CandidateProfile
                initialTab="overview"
                initialRosterTableView="all"
            />
        ),
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/career`,
        element: <CandidateProfile initialTab="career" />,
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/background`,
        element: <CandidateProfile initialTab="background" />,
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/communications`,
        element: <CandidateProfile initialTab="communications" />,
        loader: handleCandidateProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.candidates.path}/:candidateId/documents`,
        element: <CandidateProfile initialTab="documents" />,
        loader: handleCandidateProfileRouteLoad,
    },

    // Roster Profile routes
    {
        path: recruiterRouteConfig.rosters.path,
        element: <MyRosters />,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId`,
        element: <Navigate to="candidates" replace />,
        loader: handleRosterProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId/candidates`,
        element: <Navigate to="on" replace />,
        loader: handleRosterProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId/candidates/on`,
        element: <RosterProfile />,
        loader: handleRosterProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId/candidates/all`,
        element: <RosterProfile />,
        loader: handleRosterProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId/comparison`,
        element: <RosterProfile />,
        loader: handleRosterProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId/blasts`,
        element: <RosterProfile />,
        loader: handleRosterProfileRouteLoad,
    },
    {
        path: `${recruiterRouteConfig.rosters.path}/:rosterId/status-summary`,
        element: <RosterProfile />,
        loader: handleRosterProfileRouteLoad,
    },

    // Admin routes
    {
        path: recruiterRouteConfig.admin.path,
        element: <AdminDashboard />,
    },

    // Slack account linking
    {
        path: recruiterRouteConfig.slackLink.path,
        element: <SlackLink />,
    },

    // Company Profile routes
    {
        path: recruiterRouteConfig.companies.path,
        element: <Companies />,
    },
    {
        path: `${recruiterRouteConfig.companies.path}/:companyId`,
        element: <CompanyOverview />,
        loader: async ({ params }) => {
            const companyId = params.companyId ?? ''
            const queryRef = await preloadQuery(GetCompany, {
                variables: { companyId },
            }).toPromise()
            return queryRef
        },
    },
    {
        path: `${recruiterRouteConfig.companies.path}/:companyId/clientPermissions`,
        element: <ClientPermissions />,
        loader: async ({ params }) => {
            const companyId = params.companyId ?? ''
            const queryRef = await preloadQuery(GetCompany, {
                variables: { companyId },
            }).toPromise()
            return queryRef
        },
    },
]

export default RecruiterRoutes
